import { useEffect, useState } from 'react'
import { api, type Standort, type StandortEingabe } from '../api'

/* Die Standorte eines Hauses.
 *
 * Bis zum 16.09. gab es sie nur in der Datenbank: Ein Standort entstand
 * aus dem ersten Kassenarchiv, und wer ihn umbenennen oder seine
 * Kassenkennung nachtragen wollte, brauchte ein UPDATE auf der
 * Serverkonsole.
 *
 * **Was hier steht, entscheidet nicht, wer was darf.** Anlegen und
 * Ändern weist die Datenbank ab, wenn der Zugang einzelnen Standorten
 * zugeteilt ist — diese Ansicht zeigt die Formulare dann gar nicht
 * erst, aber das ist Höflichkeit und keine Sperre. Eine Rollenliste im
 * Browser wäre eine zweite Fassung der Rangordnung.
 */

const LEER: StandortEingabe = {
  name: '', strasse: '', plz: '', ort: '', ust_id: '',
  kassenkennung: '', dateimuster: '', geschaeftstag_ab: '05:00',
  zeitzone: 'Europe/Berlin',
}

function Feld({ kopf, wert, setzen, hinweis, weite, typ }: {
  kopf: string; wert: string; setzen: (w: string) => void
  hinweis?: string; weite?: number; typ?: string
}) {
  return (
    <label className="feld" style={{ flex: weite ?? 1, minWidth: 140 }}>
      <span>{kopf}</span>
      <input type={typ ?? 'text'} value={wert}
             onChange={(e) => setzen(e.target.value)} />
      {hinweis && <small className="dezent">{hinweis}</small>}
    </label>
  )
}

function Formular({ vorgabe, titel, speichern, abbrechen }: {
  vorgabe: StandortEingabe
  titel: string
  speichern: (daten: StandortEingabe) => Promise<void>
  abbrechen: () => void
}) {
  const [d, setD] = useState<StandortEingabe>(vorgabe)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const s = (k: keyof StandortEingabe) => (w: string) =>
    setD((alt) => ({ ...alt, [k]: w }))
  const v = (k: keyof StandortEingabe) => (d[k] as string | null) ?? ''

  return (
    <form className="standortform" onSubmit={(e) => {
      e.preventDefault(); setFehler(null); setLaeuft(true)
      speichern(d).catch((x: Error) => setFehler(x.message)).finally(() => setLaeuft(false))
    }}>
      <b>{titel}</b>
      <div className="feldreihe">
        <Feld kopf="Name" wert={v('name')} setzen={s('name')} weite={2}
              hinweis="So heißt der Standort in jeder Auswertung." />
      </div>
      <div className="feldreihe">
        <Feld kopf="Straße" wert={v('strasse')} setzen={s('strasse')} weite={2} />
        <Feld kopf="PLZ" wert={v('plz')} setzen={s('plz')} />
        <Feld kopf="Ort" wert={v('ort')} setzen={s('ort')} />
        <Feld kopf="USt-IdNr." wert={v('ust_id')} setzen={s('ust_id')} />
      </div>
      <div className="feldreihe">
        <Feld kopf="Kassenkennung" wert={v('kassenkennung')}
              setzen={s('kassenkennung')} weite={2}
              hinweis="Z_KASSE_ID aus dem Archiv. Daran findet jedes Archiv seinen Standort." />
        <Feld kopf="Dateimuster" wert={v('dateimuster')} setzen={s('dateimuster')}
              hinweis="Mindestens vier Zeichen. Bei kurzem Ortsnamen ein längeres Stück des echten Dateinamens eintragen; so finden die fünf Berichtsarten ihren Standort." />
      </div>
      <div className="feldreihe">
        <Feld kopf="Geschäftstag ab" typ="time" wert={v('geschaeftstag_ab')}
              setzen={s('geschaeftstag_ab')}
              hinweis="Ein Beleg von 01:30 gehört zum Vorabend." />
        <Feld kopf="Zeitzone" wert={v('zeitzone')} setzen={s('zeitzone')}
              hinweis="Jede Uhrzeit in den Auswertungen rechnet damit." />
      </div>
      {fehler && <div className="fehler">{fehler}</div>}
      <div className="knopfreihe">
        <button type="submit" disabled={laeuft || !v('name').trim()}>
          {laeuft ? 'Speichert …' : 'Speichern'}
        </button>
        <button type="button" onClick={abbrechen}>Abbrechen</button>
      </div>
    </form>
  )
}

function Zeile({ st, neu }: { st: Standort; neu: () => void }) {
  const [offen, setOffen] = useState(false)
  const anschrift = [st.strasse, [st.plz, st.ort].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ')

  return (
    <div className={`standort${st.aktiv ? '' : ' still'}`}>
      <div className="standortkopf">
        <div>
          <b>{st.name}</b> <span className="dezent">{st.code}</span>
          {!st.aktiv && <span className="marke">stillgelegt</span>}
          {st.herkunft === 'AUTOMATISCH' && !st.bestaetigt_am &&
            <span className="marke warn">noch nicht angesehen</span>}
          <div className="dezent">{anschrift || 'Keine Anschrift hinterlegt'}</div>
        </div>
        <div className="standortzahlen">
          <div><b>{st.tage}</b> <span className="dezent">Geschäftstage</span></div>
          <div className="dezent">{st.letzter_tag ?? 'noch kein Tag geladen'}</div>
        </div>
        <button type="button" onClick={() => setOffen(!offen)}>
          {offen ? 'Schließen' : 'Ändern'}
        </button>
      </div>
      <div className="standortzeilen dezent">
        <span>Kasse: {st.kassenkennung ?? '— keine hinterlegt'}</span>
        <span>Dateimuster: {st.dateimuster
          ? `„${st.dateimuster}"${st.muster_quelle === 'ORT' ? ' (aus dem Ort)' : ''}`
          : '— keins, Berichte finden diesen Standort nicht'}</span>
        <span>Geschäftstag ab {String(st.geschaeftstag_ab).slice(0, 5)} · {st.zeitzone}</span>
      </div>
      {offen && (
        <Formular
          titel={`${st.name} ändern`}
          vorgabe={{
            name: st.name, strasse: st.strasse, plz: st.plz, ort: st.ort,
            ust_id: st.ust_id, kassenkennung: st.kassenkennung,
            dateimuster: st.dateimuster,
            geschaeftstag_ab: String(st.geschaeftstag_ab).slice(0, 5),
            zeitzone: st.zeitzone,
          }}
          abbrechen={() => setOffen(false)}
          speichern={async (d) => {
            await api.haus.standortPflegen(st.id, d)
            setOffen(false); neu()
          }} />
      )}
    </div>
  )
}

export function Standorte() {
  const [liste, setListe] = useState<Standort[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [anlegen, setAnlegen] = useState(false)

  const laden = () => {
    api.haus.standorte()
      .then((d) => { setListe(d.standorte); setFehler(null) })
      .catch((e: Error) => setFehler(e.message))
  }
  useEffect(laden, [])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (liste === null) return <div className="dezent">Wird geladen …</div>

  return (
    <div className="standorte">
      <div className="standortkopfzeile">
        <h3>Standorte</h3>
        <button type="button" onClick={() => setAnlegen(!anlegen)}>
          {anlegen ? 'Abbrechen' : '+ Standort anlegen'}
        </button>
      </div>
      <p className="dezent">
        Unter der Zentrale stehen die Standorte. Jede Auswertung fasst sie
        zusammen, solange oben keiner ausgewählt ist — und ein Archiv
        findet seinen Standort über die <b>Kassenkennung</b>, jede der fünf
        Berichtsarten über das <b>Dateimuster</b>. Fehlt eins von beidem,
        bleibt die Datei liegen, statt beim falschen Standort zu landen —
        außer es gibt genau einen aktiven Standort ohne Muster, dann gehen
        die Berichte dorthin.
      </p>

      {anlegen && (
        <Formular titel="Neuer Standort" vorgabe={LEER}
                  abbrechen={() => setAnlegen(false)}
                  speichern={async (d) => {
                    await api.haus.standortAnlegen(d)
                    setAnlegen(false); laden()
                  }} />
      )}

      {liste.length === 0
        ? <div className="leer">
            Noch kein Standort. Er entsteht beim ersten Kassenarchiv — Name
            und Anschrift kommen dann aus der Betriebsstätte im Archiv.
          </div>
        : liste.map((st) => <Zeile key={st.id} st={st} neu={laden} />)}
    </div>
  )
}
